/**
 * VISTOERA_PLATFORM_ADMIN_AI_CHAT_TASK_LIST_MANAGEMENT_UI_P0 — Task List
 * table for Platform Admin → AI 管理 → AI 对话任务.
 *
 * Reuses the generic Platform Task Contract (`/platform/tasks/*`) for
 * pause/resume/delete/pin on the underlying Job row, and the existing
 * AI Chat admin routes for cancel/retry on a single Execution.
 */
import {
  CaretRightOutlined,
  DeleteOutlined,
  EyeOutlined,
  PauseOutlined,
  ReloadOutlined,
  StopOutlined,
  VerticalAlignTopOutlined,
} from '@ant-design/icons'
import type { TableColumnsType, TableProps } from 'antd'
import { Button, Empty, Modal, Table, Tooltip, message } from 'antd'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  platformBatchDeleteTasks,
  platformBatchPauseTasks,
  platformBatchResumeTasks,
  platformCancelAIChatExecution,
  platformDeleteTask,
  platformPauseTask,
  platformPinTask,
  platformResumeTask,
  platformRetryAIChatExecution,
} from '../api'
import type { AIChatAdminExecutionRow } from '../types'
import { VistoeraActionConfirm } from '../ui/action'
import { ProviderBadge, StatusBadge, WorkerStatusBadge, formatDateTime } from './adminShared'
import { platformColors } from './theme'

const BATCH_CONCURRENCY = 5

function durationLabel(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—'
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const minutes = Math.floor(seconds / 60)
  const rest = Math.round(seconds % 60)
  return `${minutes}m${rest}s`
}

function copyToClipboard(text: string) {
  navigator.clipboard
    ?.writeText(text)
    .then(() => message.success('已复制'))
    .catch(() => message.error('复制失败'))
}

function IdCell({ id }: { id: string }) {
  return (
    <Tooltip title={`${id}（点击复制）`}>
      <span
        style={{ fontFamily: 'monospace', fontSize: 12, cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation()
          copyToClipboard(id)
        }}
      >
        {id.slice(0, 8)}
      </span>
    </Tooltip>
  )
}

function WorkerCell({ row }: { row: AIChatAdminExecutionRow }) {
  if (!row.worker_id) {
    return <span style={{ color: platformColors.mutedText, fontSize: 12, fontStyle: 'italic' }}>等待分配</span>
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, flexWrap: 'wrap' }}>
      {row.worker_name || row.worker_id.slice(0, 8)}
      <WorkerStatusBadge displayStatus={row.worker_display_status} />
    </span>
  )
}

function IconAction({
  title,
  icon,
  danger,
  disabled,
  loading,
  onClick,
}: {
  title: string
  icon: React.ReactNode
  danger?: boolean
  disabled?: boolean
  loading?: boolean
  // Optional — the pause/cancel/delete triggers below are wrapped in
  // `VistoeraActionConfirm`, whose own trigger `<span>` already owns
  // click-to-open (via AntD's click-triggered `Popover`) plus its own
  // `stopPropagation` (so the click never reaches the row's `onRow`
  // handler). A redundant `onClick={(e) => e.stopPropagation()}` passed
  // straight onto THIS inner `<Button>` used to fire first and stop the
  // event from ever bubbling up to that ancestor `<span>` — which is
  // exactly where AntD/rc-trigger's click-open handler lives — so the
  // popover silently never opened (P0 fix: verified live on Preview).
  onClick?: (e: React.MouseEvent) => void
}) {
  return (
    <Tooltip title={title}>
      <Button
        type="text"
        size="small"
        icon={icon}
        danger={danger}
        disabled={disabled}
        loading={loading}
        onClick={onClick}
        style={{ width: 28, height: 28, fontSize: 16 }}
      />
    </Tooltip>
  )
}

async function runBatched<T>(items: T[], worker: (item: T) => Promise<void>, concurrency = BATCH_CONCURRENCY) {
  let index = 0
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (index < items.length) {
      const i = index++
      await worker(items[i])
    }
  })
  await Promise.all(runners)
}

export interface AIChatTaskListTableProps {
  loading: boolean
  error: string | null
  items: AIChatAdminExecutionRow[]
  total: number
  page: number
  pageSize: number
  sort: string
  sortDir: 'asc' | 'desc'
  selectedExecutionId: string | null
  onPageChange: (patch: Record<string, string | null>) => void
  onSelectExecution: (executionId: string) => void
  onReload: (opts?: { silent?: boolean }) => void
}

export default function AIChatTaskListTable({
  loading,
  error,
  items,
  total,
  page,
  pageSize,
  sort,
  sortDir,
  selectedExecutionId,
  onPageChange,
  onSelectExecution,
  onReload,
}: AIChatTaskListTableProps) {
  const { t } = useTranslation(['platformAdmin', 'actions'])
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([])
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [batchBusy, setBatchBusy] = useState<string | null>(null)

  const syncSelectionForPage = useCallback(
    (newSelectedRowKeys: React.Key[]) => {
      const pageTaskIds = items.map((r) => r.task_id).filter((id): id is string => Boolean(id))
      const newSelectedTaskIds = items
        .filter((r) => newSelectedRowKeys.includes(r.execution_id) && r.task_id)
        .map((r) => r.task_id as string)
      setSelectedTaskIds((prev) => {
        const withoutPage = prev.filter((id) => !pageTaskIds.includes(id))
        return [...withoutPage, ...newSelectedTaskIds]
      })
    },
    [items],
  )

  const selectedRowKeys = useMemo(
    () => items.filter((r) => r.task_id && selectedTaskIds.includes(r.task_id)).map((r) => r.execution_id),
    [items, selectedTaskIds],
  )

  const clearSelection = () => setSelectedTaskIds([])

  // VISTOERA_GLOBAL_ICON_ACTION_POPOVER_V1 §6 — single-row icon-triggered
  // lightweight confirmations (pause/cancel/delete below), migrated from
  // centered `Modal.confirm` to the anchored `VistoeraActionConfirm`
  // wrapping each `IconAction` trigger in the `actions` column. API/mutation
  // contract unchanged; rethrow on failure so the popover stays open with
  // the error toast instead of silently closing on a failed mutation.
  const handlePause = async (taskId: string) => {
    setBusyKey(`pause:${taskId}`)
    try {
      await platformPauseTask(taskId)
      message.success('任务已暂停')
      onReload({ silent: true })
    } catch (err) {
      message.error((err as { message?: string }).message || '暂停失败')
      onReload({ silent: true })
      throw err
    } finally {
      setBusyKey(null)
    }
  }

  const handleResume = async (taskId: string) => {
    setBusyKey(`resume:${taskId}`)
    try {
      const res = await platformResumeTask(taskId)
      message.success(res.data.message || '任务已恢复排队')
      onReload({ silent: true })
    } catch (err) {
      message.error((err as { message?: string }).message || '恢复失败')
      onReload({ silent: true })
    } finally {
      setBusyKey(null)
    }
  }

  const handlePin = async (taskId: string) => {
    setBusyKey(`pin:${taskId}`)
    try {
      const res = await platformPinTask(taskId)
      message.success(res.data.message || '已置顶')
      onReload({ silent: true })
    } catch (err) {
      message.error((err as { message?: string }).message || '置顶失败')
      onReload({ silent: true })
    } finally {
      setBusyKey(null)
    }
  }

  const handleDelete = async (taskId: string) => {
    setBusyKey(`delete:${taskId}`)
    try {
      await platformDeleteTask(taskId)
      message.success('任务已删除')
      setSelectedTaskIds((prev) => prev.filter((id) => id !== taskId))
      onReload({ silent: true })
    } catch (err) {
      message.error((err as { message?: string }).message || '删除失败')
      throw err
    } finally {
      setBusyKey(null)
    }
  }

  const handleCancel = async (executionId: string) => {
    setBusyKey(`cancel:${executionId}`)
    try {
      await platformCancelAIChatExecution(executionId)
      message.success('已取消')
      onReload({ silent: true })
    } catch (err) {
      message.error((err as { message?: string }).message || '取消失败')
      throw err
    } finally {
      setBusyKey(null)
    }
  }

  const handleRetry = async (executionId: string) => {
    setBusyKey(`retry:${executionId}`)
    try {
      const res = await platformRetryAIChatExecution(executionId)
      message.success('已创建重试任务')
      onReload({ silent: true })
      onSelectExecution(res.data.execution_id)
    } catch (err) {
      message.error((err as { message?: string }).message || '重试失败')
    } finally {
      setBusyKey(null)
    }
  }

  const doBatchPause = () => {
    Modal.confirm({
      title: `暂停 ${selectedTaskIds.length} 个任务？`,
      content: '仅排队中的任务会被暂停；其他状态将被跳过。',
      okText: '批量暂停',
      cancelText: '取消',
      onOk: async () => {
        setBatchBusy('pause')
        try {
          const res = await platformBatchPauseTasks(selectedTaskIds)
          message.success(`已暂停 ${res.data.paused_count} 个${res.data.skipped_count ? `，跳过 ${res.data.skipped_count} 个` : ''}`)
          clearSelection()
          onReload({ silent: true })
        } catch (err) {
          message.error((err as Error).message || '批量暂停失败')
        } finally {
          setBatchBusy(null)
        }
      },
    })
  }

  const doBatchResume = () => {
    Modal.confirm({
      title: `恢复 ${selectedTaskIds.length} 个任务？`,
      content: '仅已暂停的任务会被恢复；其他状态将被跳过。',
      okText: '批量恢复',
      cancelText: '取消',
      onOk: async () => {
        setBatchBusy('resume')
        try {
          const res = await platformBatchResumeTasks(selectedTaskIds)
          message.success(`已恢复 ${res.data.resumed_count} 个${res.data.skipped_count ? `，跳过 ${res.data.skipped_count} 个` : ''}`)
          clearSelection()
          onReload({ silent: true })
        } catch (err) {
          message.error((err as Error).message || '批量恢复失败')
        } finally {
          setBatchBusy(null)
        }
      },
    })
  }

  const doBatchDelete = () => {
    Modal.confirm({
      title: `确认删除已选择的 ${selectedTaskIds.length} 个任务？`,
      content: '此操作可能无法撤销。正在执行的任务将被标记为失败并移除。',
      okText: '批量删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        setBatchBusy('delete')
        try {
          const res = await platformBatchDeleteTasks(selectedTaskIds)
          const ok = res.data.deleted_count
          const fail = res.data.failed_count
          if (fail > 0) {
            message.warning(`成功 ${ok} 个，失败 ${fail} 个`)
          } else {
            message.success(`已删除 ${ok} 个任务`)
          }
          clearSelection()
          onReload({ silent: true })
        } catch (err) {
          message.error((err as Error).message || '批量删除失败')
        } finally {
          setBatchBusy(null)
        }
      },
    })
  }

  const doBatchPin = () => {
    Modal.confirm({
      title: `置顶 ${selectedTaskIds.length} 个任务？`,
      content: '仅排队中的任务会被移至队列前端；其他状态将被跳过。',
      okText: '批量置顶',
      cancelText: '取消',
      onOk: async () => {
        setBatchBusy('pin')
        let ok = 0
        let fail = 0
        await runBatched(selectedTaskIds, async (taskId) => {
          try {
            await platformPinTask(taskId)
            ok += 1
          } catch {
            fail += 1
          }
        })
        if (fail > 0) message.warning(`成功 ${ok} 个，失败 ${fail} 个`)
        else message.success(`已置顶 ${ok} 个任务`)
        clearSelection()
        onReload({ silent: true })
        setBatchBusy(null)
      },
    })
  }

  const columns: TableColumnsType<AIChatAdminExecutionRow> = [
    {
      title: '任务 ID',
      dataIndex: 'task_id',
      width: 100,
      fixed: 'left',
      render: (v: string | null) => (v ? <IdCell id={v} /> : '—'),
    },
    {
      title: '会话 ID',
      dataIndex: 'conversation_id',
      width: 110,
      render: (v: string) => <IdCell id={v} />,
    },
    {
      title: '租户',
      dataIndex: 'tenant_name',
      width: 140,
      ellipsis: true,
      render: (v: string | null, row) =>
        v ? (
          <Tooltip title={`租户 ID: ${row.workspace_id}`}>
            <span>{v}</span>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      title: '消息摘要',
      dataIndex: 'message_summary',
      width: 240,
      ellipsis: true,
      render: (v: string) => (
        <Tooltip title={v || undefined}>
          <span>{v || '—'}</span>
        </Tooltip>
      ),
    },
    {
      title: '服务商',
      dataIndex: 'provider',
      width: 100,
      render: (_: string, r) => <ProviderBadge provider={r.provider} label={r.provider_label} />,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      sorter: true,
      sortOrder: sort === 'status' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null,
      render: (_: string, r) => <StatusBadge status={r.status} label={r.status_label} />,
    },
    {
      title: 'Worker',
      width: 150,
      render: (_: unknown, r) => <WorkerCell row={r} />,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 150,
      sorter: true,
      sortOrder: sort === 'created_at' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null,
      render: (v: string | null) => formatDateTime(v),
    },
    {
      title: '开始时间',
      dataIndex: 'started_at',
      width: 150,
      sorter: true,
      sortOrder: sort === 'started_at' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null,
      render: (v: string | null) => formatDateTime(v),
    },
    {
      title: '执行耗时',
      dataIndex: 'duration_seconds',
      width: 96,
      sorter: true,
      sortOrder: sort === 'duration_seconds' ? (sortDir === 'asc' ? 'ascend' : 'descend') : null,
      render: (v: number | null) => durationLabel(v),
    },
    {
      title: '操作',
      key: 'actions',
      width: 168,
      fixed: 'right',
      className: 'pf-ai-chat-actions-col',
      render: (_: unknown, row) => {
        const taskId = row.task_id
        return (
          <div className="pf-ai-chat-actions" onClick={(e) => e.stopPropagation()}>
            <IconAction title="查看详情" icon={<EyeOutlined />} onClick={() => onSelectExecution(row.execution_id)} />
            {row.pausable && taskId && (
              <VistoeraActionConfirm
                tone="default"
                title={t('platformAdmin:aiChatTask.pause.confirmTitle')}
                description={t('platformAdmin:aiChatTask.pause.confirmDescription')}
                confirmLabel={t('platformAdmin:aiChatTask.pause.confirmLabel')}
                cancelLabel={t('actions:cancel')}
                onConfirm={() => handlePause(taskId)}
              >
                <IconAction
                  title="暂停"
                  icon={<PauseOutlined />}
                  loading={busyKey === `pause:${taskId}`}
                />
              </VistoeraActionConfirm>
            )}
            {row.resumable && taskId && (
              <IconAction
                title="恢复"
                icon={<CaretRightOutlined />}
                loading={busyKey === `resume:${taskId}`}
                onClick={() => handleResume(taskId)}
              />
            )}
            {row.pinable && taskId && (
              <IconAction
                title="置顶"
                icon={<VerticalAlignTopOutlined />}
                loading={busyKey === `pin:${taskId}`}
                onClick={() => handlePin(taskId)}
              />
            )}
            {row.retryable && (
              <IconAction
                title="重试"
                icon={<ReloadOutlined />}
                loading={busyKey === `retry:${row.execution_id}`}
                onClick={() => handleRetry(row.execution_id)}
              />
            )}
            {row.cancellable && (
              <VistoeraActionConfirm
                title={t('platformAdmin:aiChatTask.cancel.confirmTitle')}
                description={t('platformAdmin:aiChatTask.cancel.confirmDescription')}
                confirmLabel={t('platformAdmin:aiChatTask.cancel.confirmLabel')}
                cancelLabel={t('actions:back')}
                onConfirm={() => handleCancel(row.execution_id)}
              >
                <IconAction
                  title="取消"
                  icon={<StopOutlined />}
                  danger
                  loading={busyKey === `cancel:${row.execution_id}`}
                />
              </VistoeraActionConfirm>
            )}
            {row.deletable && taskId && (
              <VistoeraActionConfirm
                title={t('platformAdmin:aiChatTask.delete.confirmTitle')}
                description={t('platformAdmin:aiChatTask.delete.confirmDescriptionWithId', { taskId: taskId.slice(0, 8) })}
                confirmLabel={t('platformAdmin:aiChatTask.delete.confirmLabel')}
                cancelLabel={t('actions:cancel')}
                onConfirm={() => handleDelete(taskId)}
              >
                <IconAction
                  title="删除"
                  icon={<DeleteOutlined />}
                  danger
                  loading={busyKey === `delete:${taskId}`}
                />
              </VistoeraActionConfirm>
            )}
          </div>
        )
      },
    },
  ]

  const handleTableChange: TableProps<AIChatAdminExecutionRow>['onChange'] = (pagination, _f, sorter) => {
    const patch: Record<string, string | null> = {}
    if (pagination.current) patch.page = String(pagination.current)
    if (pagination.pageSize) patch.page_size = String(pagination.pageSize)
    const s = Array.isArray(sorter) ? sorter[0] : sorter
    if (s?.field) {
      patch.sort = String(s.field)
      patch.sort_dir = s.order === 'ascend' ? 'asc' : s.order === 'descend' ? 'desc' : null
    }
    onPageChange(patch)
  }

  return (
    <div style={{ background: platformColors.surface, border: `1px solid ${platformColors.border}`, borderRadius: 8, overflow: 'hidden' }}>
      {selectedTaskIds.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            background: platformColors.infoBg,
            borderBottom: `1px solid ${platformColors.border}`,
            padding: '8px 12px',
            fontSize: 13,
          }}
        >
          <span style={{ color: platformColors.primaryText }}>已选择 {selectedTaskIds.length} 项</span>
          <Tooltip title="暂停">
            <Button size="small" icon={<PauseOutlined />} loading={batchBusy === 'pause'} onClick={doBatchPause} />
          </Tooltip>
          <Tooltip title="恢复">
            <Button size="small" icon={<CaretRightOutlined />} loading={batchBusy === 'resume'} onClick={doBatchResume} />
          </Tooltip>
          <Tooltip title="删除">
            <Button size="small" danger icon={<DeleteOutlined />} loading={batchBusy === 'delete'} onClick={doBatchDelete} />
          </Tooltip>
          <Tooltip title="置顶">
            <Button size="small" icon={<VerticalAlignTopOutlined />} loading={batchBusy === 'pin'} onClick={doBatchPin} />
          </Tooltip>
          <Button size="small" onClick={clearSelection}>
            取消选择
          </Button>
        </div>
      )}

      {error ? (
        <div style={{ padding: 24 }}>
          <Empty description={error} />
        </div>
      ) : (
        <Table
          className="pf-data-table pf-ai-chat-task-table"
          rowKey="execution_id"
          size="small"
          loading={loading}
          dataSource={items}
          columns={columns}
          scroll={{ x: 1380 }}
          sticky
          onChange={handleTableChange}
          rowSelection={{
            selectedRowKeys,
            preserveSelectedRowKeys: true,
            columnWidth: 44,
            fixed: true,
            onChange: (keys) => syncSelectionForPage(keys),
            getCheckboxProps: (row) => ({ disabled: !row.task_id, name: row.task_id ?? row.execution_id }),
          }}
          onRow={(r) => ({
            onClick: () => onSelectExecution(r.execution_id),
            style: {
              cursor: 'pointer',
              background: r.execution_id === selectedExecutionId ? 'var(--pf-primary-soft)' : undefined,
            },
          })}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (t) => `共 ${t} 条`,
          }}
        />
      )}
    </div>
  )
}
